import type { UIMessage } from 'ai';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import { bucketLength, useAnalytics } from '~/analytics';
import {
  makeSessionTitle,
  migrateLegacyIfPresent,
  readStored,
  type StoredSession,
  type StoredV2,
  writeStored,
} from '~/components/otto/sessions';

export type OttoMode = 'closed' | 'dock' | 'full';

export type SessionStatus = 'ready' | 'submitted' | 'streaming' | 'error';

export type Session = StoredSession & {
  draft: string;
  status: SessionStatus;
  errorMessage: string | null;
  // In-memory only (not persisted): the invoice this session was opened from,
  // when started via startConversation({ invoiceId }). Used for analytics
  // attribution of the rule-suggestion funnel back to the originating invoice.
  invoiceId?: string | null;
};

type OttoState = {
  mode: OttoMode;
  historyOpen: boolean;
  activeSessionId: string | null;
  sessions: Record<string, Session>;
  sessionOrder: string[];
  examplePropertyName: string | null;
};

export type ConversationHandle = {
  send: (text: string) => void;
  stop: () => void;
};

type Action =
  | { type: 'openDock' }
  | { type: 'expand' }
  | { type: 'dock' }
  | { type: 'close' }
  | { type: 'toggleHistory' }
  | { type: 'newChat'; id: string; now: number; invoiceId?: string | null }
  | { type: 'selectSession'; id: string; now: number }
  | { type: 'deleteSession'; id: string }
  | { type: 'setDraft'; id: string; value: string }
  | { type: 'consumeDraft'; id: string }
  | { type: 'messages'; id: string; messages: UIMessage[]; now: number }
  | { type: 'assistantArrived'; id: string; now: number }
  | { type: 'view'; id: string; now: number }
  | { type: 'hydrate'; stored: StoredV2 }
  | { type: 'setExamplePropertyName'; value: string | null }
  | { type: 'status'; id: string; value: SessionStatus; errorMessage: string | null };

function emptySession(id: string, now: number): Session {
  return {
    id,
    title: 'New chat',
    createdAt: now,
    messages: [],
    lastAssistantAt: null,
    unread: false,
    draft: '',
    status: 'ready',
    errorMessage: null,
  };
}

function reducer(state: OttoState, action: Action): OttoState {
  switch (action.type) {
    case 'openDock':
      return { ...state, mode: state.mode === 'full' ? 'full' : 'dock' };
    case 'expand':
      return { ...state, mode: 'full', historyOpen: true };
    case 'dock':
      return { ...state, mode: 'dock', historyOpen: false };
    case 'close':
      return { ...state, mode: 'closed', historyOpen: false };
    case 'toggleHistory':
      return { ...state, historyOpen: !state.historyOpen };
    case 'newChat': {
      const sessions = {
        ...state.sessions,
        [action.id]: {
          ...emptySession(action.id, action.now),
          invoiceId: action.invoiceId ?? null,
        },
      };
      return {
        ...state,
        sessions,
        sessionOrder: [action.id, ...state.sessionOrder],
        activeSessionId: action.id,
        mode: state.mode === 'closed' ? 'dock' : state.mode,
      };
    }
    case 'selectSession': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      const sessions: Record<string, Session> = {
        ...state.sessions,
        [action.id]: { ...existing, unread: false },
      };
      return {
        ...state,
        sessions,
        activeSessionId: action.id,
        sessionOrder: [action.id, ...state.sessionOrder.filter((id) => id !== action.id)],
      };
    }
    case 'deleteSession': {
      if (!state.sessions[action.id]) return state;
      const sessionOrder = state.sessionOrder.filter((id) => id !== action.id);
      const sessions = { ...state.sessions };
      delete sessions[action.id];
      const activeSessionId =
        state.activeSessionId === action.id ? (sessionOrder[0] ?? null) : state.activeSessionId;
      return { ...state, sessions, sessionOrder, activeSessionId };
    }
    case 'setDraft': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      return {
        ...state,
        sessions: { ...state.sessions, [action.id]: { ...existing, draft: action.value } },
      };
    }
    case 'consumeDraft': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      return {
        ...state,
        sessions: { ...state.sessions, [action.id]: { ...existing, draft: '' } },
      };
    }
    case 'messages': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      const title =
        existing.title === 'New chat' && action.messages.length > 0
          ? makeSessionTitle(action.messages)
          : existing.title;
      // Detect a brand-new assistant turn by comparing assistant-message
      // counts against the session's previous messages — must be done here
      // in the reducer (not in the caller) so we see the latest state and
      // don't race with the parent's ref-syncing effect during hydrate.
      // Count-based comparison catches every new turn, not just the first
      // one in the session.
      const countAssistant = (msgs: UIMessage[]) =>
        msgs.reduce((n, m) => n + (m.role === 'assistant' ? 1 : 0), 0);
      const assistantBefore = countAssistant(existing.messages);
      const assistantNow = countAssistant(action.messages);
      const isNewAssistantTurn = assistantNow > assistantBefore;
      const lastAssistantAt = isNewAssistantTurn ? action.now : existing.lastAssistantAt;
      // A new assistant turn only counts as "unread" when the user isn't
      // looking. If they are looking, we keep the existing `unread` value
      // (which the open/select path already cleared).
      const isBeingViewed = state.mode !== 'closed' && state.activeSessionId === action.id;
      const unread = isNewAssistantTurn && !isBeingViewed ? true : existing.unread;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [action.id]: {
            ...existing,
            title,
            messages: action.messages,
            lastAssistantAt,
            unread,
          },
        },
      };
    }
    case 'assistantArrived': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      const isBeingViewed = state.mode !== 'closed' && state.activeSessionId === action.id;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [action.id]: {
            ...existing,
            lastAssistantAt: action.now,
            unread: isBeingViewed ? existing.unread : true,
          },
        },
      };
    }
    case 'view': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      if (!existing.unread) return state;
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [action.id]: { ...existing, unread: false },
        },
      };
    }
    case 'hydrate': {
      const sessions: Record<string, Session> = {};
      for (const id of action.stored.sessionOrder) {
        const s = action.stored.sessions[id];
        if (s) sessions[id] = { ...s, draft: '', status: 'ready', errorMessage: null };
      }
      return {
        ...state,
        activeSessionId: action.stored.activeSessionId,
        sessionOrder: action.stored.sessionOrder,
        sessions,
      };
    }
    case 'setExamplePropertyName':
      return { ...state, examplePropertyName: action.value };
    case 'status': {
      const existing = state.sessions[action.id];
      if (!existing) return state;
      if (existing.status === action.value && existing.errorMessage === action.errorMessage) {
        return state;
      }
      return {
        ...state,
        sessions: {
          ...state.sessions,
          [action.id]: { ...existing, status: action.value, errorMessage: action.errorMessage },
        },
      };
    }
  }
}

const INITIAL: OttoState = {
  mode: 'closed',
  historyOpen: false,
  activeSessionId: null,
  sessions: {},
  sessionOrder: [],
  examplePropertyName: null,
};

type ContextValue = {
  state: OttoState;
  openDock(): void;
  expand(): void;
  dock(): void;
  close(): void;
  toggleHistory(): void;
  newChat(): string;
  startConversation(text: string, opts?: { invoiceId?: string }): string;
  selectSession(id: string): void;
  deleteSession(id: string): void;
  setDraft(id: string, value: string): void;
  submitDraft(): void;
  sendMessage(id: string, text: string): boolean;
  stopActive(): void;
  registerConversation(id: string, handle: ConversationHandle): () => void;
  onMessagesChange(id: string, messages: UIMessage[]): void;
  onStatusChange(id: string, status: SessionStatus, errorMessage: string | null): void;
  markAssistantArrived(id: string): void;
};

const OttoContext = createContext<ContextValue | null>(null);

export function OttoProvider({
  children,
  examplePropertyName,
  active = true,
}: {
  children: ReactNode;
  examplePropertyName: string | null;
  /**
   * `false` publishes no context: `useOttoOptional()` returns `null` and
   * `useOttoModeOrClosed()` returns `'closed'`, the same as with no provider mounted.
   * `OttoIntegration` uses it to keep the provider in the tree while the `aiChat` flag
   * is off, so the flag turning on changes this value rather than the element type
   * above the app shell.
   */
  active?: boolean;
}) {
  const [state, dispatch] = useReducer(reducer, {
    ...INITIAL,
    examplePropertyName,
  });
  const { track } = useAnalytics();

  // Mirror of state.sessions for effect bodies that need a current
  // snapshot without re-running on every sessions change. The reducer
  // remains the source of truth; this ref just lets the view effect read
  // the pre-dispatch unread state to emit chat_unread_indicator_dismissed
  // exactly once per real dismissal.
  const sessionsRef = useRef(state.sessions);
  useEffect(() => {
    sessionsRef.current = state.sessions;
  }, [state.sessions]);

  useEffect(() => {
    dispatch({ type: 'setExamplePropertyName', value: examplePropertyName });
  }, [examplePropertyName]);

  const handles = useRef<Map<string, ConversationHandle>>(new Map());
  // A message queued by startConversation() to auto-send as soon as the new
  // session's conversation handle registers. Lets callers (e.g. the invoice
  // reject dialog) open the chat with a pre-filled turn without racing the
  // runner's mount.
  const pendingAutoSendRef = useRef<{ id: string; text: string } | null>(null);
  // Skip the first persist run so the empty initial state never clobbers
  // stored data before the hydrate effect has had a chance to load it.
  // After this gate flips, subsequent state changes (including deleting
  // the only remaining session) always persist.
  const firstPersistSkipped = useRef(false);

  useEffect(() => {
    const legacy = migrateLegacyIfPresent();
    const stored = legacy ?? readStored();
    if (stored.sessionOrder.length > 0 || stored.activeSessionId != null) {
      dispatch({ type: 'hydrate', stored });
    }
  }, []);

  useEffect(() => {
    if (!firstPersistSkipped.current) {
      firstPersistSkipped.current = true;
      return;
    }
    const stored: StoredV2 = {
      version: 2,
      activeSessionId: state.activeSessionId,
      sessionOrder: state.sessionOrder,
      sessions: Object.fromEntries(
        state.sessionOrder
          .map((id) => state.sessions[id])
          .filter((s): s is Session => Boolean(s))
          .map((s) => [
            s.id,
            {
              id: s.id,
              title: s.title,
              createdAt: s.createdAt,
              messages: s.messages,
              lastAssistantAt: s.lastAssistantAt,
              unread: s.unread,
            } satisfies StoredSession,
          ]),
      ),
    };
    const handle = window.setTimeout(() => writeStored(stored), 250);
    return () => window.clearTimeout(handle);
  }, [state.activeSessionId, state.sessionOrder, state.sessions]);

  useEffect(() => {
    if (state.mode === 'closed') return;
    if (!state.activeSessionId) return;
    // Read sessions through the ref so this effect only re-runs on the
    // transitions that actually open/switch a session — not on every
    // message arrival. Without the ref, adding sessions to the deps
    // would fire chat_unread_indicator_dismissed each time the unread
    // session receives a new message in the background.
    const session = sessionsRef.current[state.activeSessionId];
    if (session?.unread) {
      track('chat_unread_indicator_dismissed', { session_id: state.activeSessionId });
    }
    dispatch({ type: 'view', id: state.activeSessionId, now: Date.now() });
  }, [state.mode, state.activeSessionId, track]);

  const registerConversation = useCallback((id: string, handle: ConversationHandle) => {
    handles.current.set(id, handle);
    // If this session was opened with a queued message, send it now that the
    // runner is mounted and able to stream.
    const pending = pendingAutoSendRef.current;
    if (pending && pending.id === id) {
      pendingAutoSendRef.current = null;
      handle.send(pending.text);
    }
    return () => {
      if (handles.current.get(id) === handle) handles.current.delete(id);
    };
  }, []);

  const onMessagesChange = useCallback((id: string, messages: UIMessage[]) => {
    dispatch({ type: 'messages', id, messages, now: Date.now() });
  }, []);

  const onStatusChange = useCallback(
    (id: string, status: SessionStatus, errorMessage: string | null) => {
      dispatch({ type: 'status', id, value: status, errorMessage });
    },
    [],
  );

  const submitDraft = useCallback(() => {
    const id = state.activeSessionId;
    if (!id) return;
    const session = state.sessions[id];
    if (!session) return;
    if (session.status === 'submitted' || session.status === 'streaming') return;
    const text = session.draft.trim();
    if (!text) return;
    const handle = handles.current.get(id);
    if (!handle) return;

    // Lazy chat_session_created: fires when the first message lands,
    // not when newChat() mints an empty session. Keeps the
    // "session_created → message_sent" funnel honest — empty sessions
    // (created by mis-clicks or starter-question previews that never
    // get sent) don't pollute the numerator.
    const userMessagesBefore = session.messages.reduce(
      (n, m) => n + (m.role === 'user' ? 1 : 0),
      0,
    );
    if (userMessagesBefore === 0) {
      track('chat_session_created', { session_id: id });
    }
    track('chat_message_sent', {
      session_id: id,
      turn_index: userMessagesBefore,
      message_length_bucket: bucketLength(text),
    });

    handle.send(text);
    dispatch({ type: 'consumeDraft', id });
  }, [state.activeSessionId, state.sessions, track]);

  const sendMessage = useCallback(
    (id: string, text: string): boolean => {
      const session = state.sessions[id];
      const trimmed = text.trim();
      if (!session || !trimmed) return false;
      if (session.status === 'submitted' || session.status === 'streaming') return false;
      const handle = handles.current.get(id);
      if (!handle) return false;
      const userMessagesBefore = session.messages.reduce(
        (n, m) => n + (m.role === 'user' ? 1 : 0),
        0,
      );
      if (userMessagesBefore === 0) track('chat_session_created', { session_id: id });
      track('chat_message_sent', {
        session_id: id,
        turn_index: userMessagesBefore,
        message_length_bucket: bucketLength(trimmed),
      });
      handle.send(trimmed);
      return true;
    },
    [state.sessions, track],
  );

  const stopActive = useCallback(() => {
    const id = state.activeSessionId;
    if (!id) return;
    const handle = handles.current.get(id);
    if (!handle) return;
    handle.stop();
  }, [state.activeSessionId]);

  const deleteSession = useCallback((id: string) => {
    handles.current.get(id)?.stop();
    dispatch({ type: 'deleteSession', id });
  }, []);

  // Wrapped so we can fire chat_session_switched on real selections and
  // skip the no-op case where the user clicks the already-active row.
  // The reducer guards against the same case for state, but firing the
  // event there too would inflate the switch funnel.
  const selectSession = useCallback(
    (id: string) => {
      const fromSessionId = state.activeSessionId;
      if (id === fromSessionId) return;
      track('chat_session_switched', {
        from_session_id: fromSessionId,
        to_session_id: id,
      });
      dispatch({ type: 'selectSession', id, now: Date.now() });
    },
    [state.activeSessionId, track],
  );

  const value = useMemo<ContextValue>(
    () => ({
      state,
      openDock: () => dispatch({ type: 'openDock' }),
      expand: () => dispatch({ type: 'expand' }),
      dock: () => dispatch({ type: 'dock' }),
      close: () => dispatch({ type: 'close' }),
      toggleHistory: () => dispatch({ type: 'toggleHistory' }),
      newChat: () => {
        // Return the new session id so callers can chain follow-up
        // actions (e.g. setDraft from a starter-question chip) on the
        // freshly-created session without having to read activeSessionId
        // after the reducer has applied this dispatch.
        const id = crypto.randomUUID();
        dispatch({ type: 'newChat', id, now: Date.now() });
        return id;
      },
      startConversation: (text: string, opts?: { invoiceId?: string }) => {
        // Open a fresh session and queue `text` to auto-send once the
        // runner registers (see registerConversation). Used by callers
        // outside the chat UI — e.g. the invoice reject dialog's
        // "Generate review rules" action — to hand the agent a pre-filled
        // turn. Mirrors submitDraft's lazy session_created + message_sent
        // instrumentation so the funnel stays honest. `opts.invoiceId` tags
        // the session for rule-suggestion analytics attribution.
        const trimmed = text.trim();
        const id = crypto.randomUUID();
        dispatch({ type: 'newChat', id, now: Date.now(), invoiceId: opts?.invoiceId });
        if (trimmed) {
          pendingAutoSendRef.current = { id, text: trimmed };
          track('chat_session_created', { session_id: id });
          track('chat_message_sent', {
            session_id: id,
            turn_index: 0,
            message_length_bucket: bucketLength(trimmed),
          });
        }
        dispatch({ type: 'openDock' });
        return id;
      },
      selectSession,
      deleteSession,
      setDraft: (id, value) => dispatch({ type: 'setDraft', id, value }),
      submitDraft,
      sendMessage,
      stopActive,
      registerConversation,
      onMessagesChange,
      onStatusChange,
      markAssistantArrived: (id) => dispatch({ type: 'assistantArrived', id, now: Date.now() }),
    }),
    [
      state,
      track,
      submitDraft,
      sendMessage,
      stopActive,
      deleteSession,
      selectSession,
      registerConversation,
      onMessagesChange,
      onStatusChange,
    ],
  );

  return <OttoContext.Provider value={active ? value : null}>{children}</OttoContext.Provider>;
}

export function useOtto(): ContextValue {
  const ctx = useContext(OttoContext);
  if (!ctx) throw new Error('useOtto must be used inside <OttoProvider>');
  return ctx;
}

export function useOttoModeOrClosed(): OttoMode {
  const ctx = useContext(OttoContext);
  return ctx?.state.mode ?? 'closed';
}

// Safe accessor for callers that live outside the chat UI and may render
// when Otto is disabled (no provider mounted) — returns null instead of
// throwing. Used by the invoice reject dialog's rule-suggestion CTA.
export function useOttoOptional(): ContextValue | null {
  return useContext(OttoContext);
}
